import { useEffect, useMemo, useState } from "react";
import { CaretLeft, CaretRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { api } from "../../lib/api";
import { NetworkDot, STATUS, errMsg } from "./socialShared";

const DAYS = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];
const key = (d) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;

function buildGrid(month) {
  const first = new Date(month.getFullYear(), month.getMonth(), 1);
  const start = new Date(first);
  start.setDate(1 - ((first.getDay() + 6) % 7));
  return Array.from({ length: 42 }, (_, i) => { const d = new Date(start); d.setDate(start.getDate() + i); return d; });
}

function DayCell({ day, month, posts, onOpen, onNew }) {
  const today = key(day) === key(new Date());
  const inMonth = day.getMonth() === month.getMonth();
  const past = new Date(day.getFullYear(), day.getMonth(), day.getDate(), 23, 59) < new Date();
  return (
    <div className={`min-h-[96px] border-b border-r p-1.5 ${inMonth ? "bg-card" : "bg-muted/40"} ${!past ? "cursor-pointer hover:bg-muted/60" : ""}`}
      onClick={() => !past && onNew(day)} data-testid={`calendar-day-${day.getDate()}-${day.getMonth() + 1}`}>
      <div className={`mb-1 text-xs ${today ? "inline-flex h-5 w-5 items-center justify-center rounded-full bg-foreground text-background font-semibold" : inMonth ? "text-foreground" : "text-muted-foreground"}`}>{day.getDate()}</div>
      <div className="space-y-1">
        {posts.slice(0, 3).map((p) => (
          <button key={p.id} type="button" onClick={(e) => { e.stopPropagation(); onOpen(p); }}
            className={`flex w-full items-center gap-1 truncate rounded border px-1 py-0.5 text-left text-[10px] ${STATUS[p.status]?.cls || ""}`}
            data-testid={`calendar-post-${p.id}`}>
            <span className="shrink-0 font-mono">{new Date(p.scheduled_at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}</span>
            {p.networks.map((n) => <NetworkDot key={n} id={n} size={10} />)}
            <span className="truncate">{p.text}</span>
          </button>
        ))}
        {posts.length > 3 && <div className="text-[10px] text-muted-foreground">+{posts.length - 3} autre(s)</div>}
      </div>
    </div>
  );
}

export function SocialCalendar({ refreshKey, onOpen, onNew }) {
  const [month, setMonth] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [posts, setPosts] = useState([]);
  const grid = useMemo(() => buildGrid(month), [month]);

  useEffect(() => {
    const start = grid[0].toISOString();
    const end = new Date(grid[41].getTime() + 86400000).toISOString();
    api.get("/social/posts", { params: { start, end, per_page: 500 } })
      .then((r) => setPosts(r.data.items))
      .catch((e) => toast.error(errMsg(e)));
  }, [grid, refreshKey]);

  const byDay = useMemo(() => {
    const m = {};
    posts.forEach((p) => { if (p.scheduled_at) { const k = key(new Date(p.scheduled_at)); (m[k] = m[k] || []).push(p); } });
    Object.values(m).forEach((l) => l.sort((a, b) => a.scheduled_at.localeCompare(b.scheduled_at)));
    return m;
  }, [posts]);

  const shift = (n) => setMonth(new Date(month.getFullYear(), month.getMonth() + n, 1));
  const newAt = (day) => { const d = new Date(day); d.setHours(10, 0, 0, 0); if (d < new Date()) { d.setTime(Date.now() + 3600000); } onNew(d.toISOString()); };

  return (
    <div data-testid="social-calendar">
      <div className="mb-3 flex items-center gap-2">
        <Button variant="outline" size="icon" onClick={() => shift(-1)} data-testid="calendar-prev"><CaretLeft size={14} /></Button>
        <Button variant="outline" size="icon" onClick={() => shift(1)} data-testid="calendar-next"><CaretRight size={14} /></Button>
        <Button variant="ghost" size="sm" onClick={() => { const d = new Date(); setMonth(new Date(d.getFullYear(), d.getMonth(), 1)); }} data-testid="calendar-today">Aujourd'hui</Button>
        <h2 className="ml-2 text-lg font-semibold capitalize" data-testid="calendar-month-label">{month.toLocaleDateString("fr-FR", { month: "long", year: "numeric" })}</h2>
      </div>
      <div className="overflow-x-auto rounded-xl border">
        <div className="grid min-w-[700px] grid-cols-7 border-l border-t">
          {DAYS.map((d) => <div key={d} className="border-b border-r bg-muted px-2 py-1 text-xs font-medium text-muted-foreground">{d}</div>)}
          {grid.map((d) => <DayCell key={key(d)} day={d} month={month} posts={byDay[key(d)] || []} onOpen={onOpen} onNew={newAt} />)}
        </div>
      </div>
      <p className="mt-2 text-xs text-muted-foreground">Cliquez sur un jour pour programmer un post, ou sur un post pour l'ouvrir.</p>
    </div>
  );
}
